import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTable } from "react-table";
import classNames from "classnames";
import { compose, path, prop, propEq, sortBy } from "ramda";
import { Maybe } from "ramda-fantasy";
import { useI18n } from "@quantum/i18n";
import {
  getGrupamentoMedidaSelecionado,
  getMedidasPorGrupamento,
  getFiltrosTipoAtivo,
  getGrupamentosMedidaRelacionamentos,
  getMapConjuntoPermissaoMedidasPorTipoAtivo,
  getTiposDeAtivosPermitidos,
} from "../../../state";
import RadioGroup from "../../comum/RadioGroup";
import { normalizarString } from "../../../util";

import { ReactComponent as IndicadorMedidaPermitidaEAplicavel } from "../../../assets/relacionamento_medida_permitida.svg";
import { ReactComponent as IndicadorMedidaNaoPermitidaEAplicavel } from "../../../assets/relacionamento_medida_nao_permitida.svg";
import "./relacionamentos.css";

const MEDIDA_NAO_SE_APLICA_AO_TIPO_ATIVO: number = 0;
const MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_TEM_PERMISSAO: number = 1;
const MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_NAO_TEM_PERMISSAO: number = 2;

const CellRelacionamento = ({ cell: { value } }) => {
  switch (value) {
    case MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_TEM_PERMISSAO:
      return <IndicadorMedidaPermitidaEAplicavel />;
    case MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_NAO_TEM_PERMISSAO:
      return <IndicadorMedidaNaoPermitidaEAplicavel />;
    default:
      return null;
  }
};

const processaRelacionamento = (
  filtrosMedida,
  conjuntoPermissaoMedida,
  mapPermissoes,
  tiposDeAtivosPermitidos,
  idTipoAtivo,
  nomeTipoAtivo,
  todosTiposDeAtivos
) => {
  if (!filtrosMedida.includes(idTipoAtivo)) {
    return MEDIDA_NAO_SE_APLICA_AO_TIPO_ATIVO;
  }

  if (
    !mapPermissoes[nomeTipoAtivo] &&
    !tiposDeAtivosPermitidos.includes(nomeTipoAtivo)
  ) {
    return MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_NAO_TEM_PERMISSAO;
  }

  if (
    mapPermissoes[nomeTipoAtivo] &&
    mapPermissoes[nomeTipoAtivo].includes(conjuntoPermissaoMedida)
  ) {
    // Nesse caso o usu�rio tem permiss�o direta ao conjunto de permissao da medida
    return MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_TEM_PERMISSAO;
  }

  // Nesse caso � preciso verificar se o usu�rio tem permiss�o indireta a medida
  // Por exemplo, Retorno � do conjunto 0 e se aplica a FI e A��es entre outros.
  // Um usu�rio que tenha acesso ao conjunto 0 de FI e n�o tenha acesso ao conjunto 0 de A��es,
  // por�m TEM acesso a A��es consegue usar essa medida indiretamente,
  // logo tem que vir marcado no relacionamento
  const todosTiposDeAtivosDaMedida = todosTiposDeAtivos.filter(filtro =>
    filtrosMedida.includes(filtro.id)
  );
  return todosTiposDeAtivosDaMedida.some(
    ({ nome }) =>
      mapPermissoes[nome] &&
      mapPermissoes[nome].includes(conjuntoPermissaoMedida)
  ) === true
    ? MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_TEM_PERMISSAO
    : MEDIDA_SE_APLICA_AO_TIPO_ATIVO_E_USUARIO_NAO_TEM_PERMISSAO;
};

function Relacionamentos() {
  const { translate } = useI18n();
  const grupamentos = useSelector(getGrupamentosMedidaRelacionamentos);
  const tiposDeAtivos = useSelector(getFiltrosTipoAtivo);
  const mapConjuntoPermissaoMedidas = useSelector(
    getMapConjuntoPermissaoMedidasPorTipoAtivo
  );
  const tiposDeAtivosPermitidos = useSelector(getTiposDeAtivosPermitidos);
  const grupamentoSelecionado = useSelector(getGrupamentoMedidaSelecionado);

  const [grupamento, defineGrupamento] = useState(
    Maybe(grupamentos.find(propEq("nome", grupamentoSelecionado)))
      .map(prop("nome"))
      .getOrElse(grupamentos[0].nome)
  );
  const selecionaGrupamento = useCallback(
    event => {
      defineGrupamento(event.target.value);
    },
    [defineGrupamento]
  );

  const medidas = useSelector(state =>
    getMedidasPorGrupamento(state, grupamento)
  );

  const columns = useMemo(
    () => [
      { Header: translate("maiusculas.campo"), accessor: "nome" },

      ...tiposDeAtivos.map((tipoAtivo, i) => ({
        Header: translate(tipoAtivo.chaveTraducao).replace("/", " "),
        id: tipoAtivo.id,
        accessor: path(["relacionamentos", i]),
        Cell: CellRelacionamento,
      })),
    ],
    [tiposDeAtivos, translate]
  );

  const data = useMemo(
    () =>
      sortBy(
        compose(normalizarString, prop("nome")),
        medidas.map(({ nome, filtros, conjuntoPermissao }) => {
          const chaveTraducao: string = nome.replace(/ /g, "");
          const traducao: ?string = translate(chaveTraducao);
          return {
            nome: traducao === chaveTraducao ? nome : traducao,
            relacionamentos: tiposDeAtivos.map(({ id, nome }) =>
              processaRelacionamento(
                filtros,
                conjuntoPermissao,
                mapConjuntoPermissaoMedidas,
                tiposDeAtivosPermitidos,
                id,
                nome,
                tiposDeAtivos
              )
            ),
          };
        })
      ),
    [
      medidas,
      tiposDeAtivos,
      mapConjuntoPermissaoMedidas,
      tiposDeAtivosPermitidos,
      translate,
    ]
  );

  const { getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  return (
    <div>
      <div className="Relacionamentos__grupamentos">
        <RadioGroup
          nome="grupamento"
          itens={grupamentos}
          campoId="nome"
          campoDescricao="traducao"
          onChange={selecionaGrupamento}
          itemSelecionado={grupamento}
        />
      </div>

      <table className="Relacionamentos__tabela">
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, i) => (
                <td
                  {...column.getHeaderProps()}
                  className={classNames({
                    "Relacionamentos__coluna-medida": i === 0,
                    "Relacionamentos__coluna-tipo-ativo": i !== 0,
                  })}
                >
                  {column.render("Header")}
                </td>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell, i) => (
                  <td
                    {...cell.getCellProps()}
                    className={classNames({
                      "Relacionamentos__coluna-medida": i === 0,
                      "Relacionamentos__coluna-tipo-ativo": i !== 0,
                    })}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Relacionamentos;
